import React, { useEffect, useState } from "react";
import { Linkedin, X } from "lucide-react";
import basedPrimaryLogo from "/assets/based-primary.png";
import basedWhiteLogo from "/assets/based-white.png";
import { Link } from "react-router-dom";
import { usePageTracking, trackEvent } from "../hooks/usePageTracking";
import useScrollToTop from "../hooks/useScrollToTop";

interface LayoutProps {
  children: React.ReactNode;
  navbarBackground?: string;
}

export function Layout({ children, navbarBackground }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  usePageTracking();
  useScrollToTop();

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Close mobile menu when navigating to a new page
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Set body background color based on navbarBackground
  useEffect(() => {
    if (navbarBackground === "black") {
      document.body.classList.add("bg-black");
    } else {
      document.body.classList.remove("bg-black");
    }

    // Cleanup function to remove class when component unmounts
    return () => {
      document.body.classList.remove("bg-black");
    };
  }, [navbarBackground]);

  const navbarBgClass =
    navbarBackground === "black"
      ? "bg-black/80 text-white"
      : "bg-white/90 text-gray-900";

  // Use the appropriate logo based on navbar background
  const logoSrc =
    navbarBackground === "black" ? basedWhiteLogo : basedPrimaryLogo;

  return (
    <div className="flex flex-col min-h-screen">
      {/* Navigation */}
      <nav
        className={`fixed top-6 left-0 right-0 z-50 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8`}>
        <div
          className={`${navbarBgClass} rounded-full shadow-lg transition-all duration-300 backdrop-blur-md border border-gray-200/10 ${
            navbarBackground === "black" ? "gold-nav-animation" : ""
          }`}>
          <div className="flex justify-between h-16 px-6">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <img src={logoSrc} alt="Based App" className="h-8 w-auto" />
              </Link>
            </div>

            {/* Nav Links - Adjust text color based on background */}
            <div className="hidden md:flex md:items-center md:space-x-8">
              <Link
                to="/hyperapp"
                onClick={() =>
                  trackEvent("Navigation", "Click", "Security Page")
                }
                className={
                  navbarBackground === "black"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }>
                HyperApp
              </Link>
              <Link
                to="/security"
                onClick={() =>
                  trackEvent("Navigation", "Click", "Security Page")
                }
                className={
                  navbarBackground === "black"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }>
                Security
              </Link>
              <Link
                to="/gold"
                className={
                  navbarBackground === "black"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }>
                Gold
              </Link>
              <Link
                to="/"
                className={
                  navbarBackground === "black"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }>
                Blog
              </Link>
              <Link
                to="/faq"
                className={
                  navbarBackground === "black"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }>
                FAQs
              </Link>
            </div>

            {/* CTA Buttons - Keep consistent regardless of background */}
            <div className="hidden md:flex md:items-center md:space-x-4">
              <a
                href="https://app.basedapp.io"
                target="_blank"
                rel="noopener noreferrer"
                className={`px-3 py-1.5 rounded-full transition-all duration-200 text-sm font-medium ${
                  navbarBackground === "black"
                    ? "text-gray-300 hover:text-white hover:bg-white/10"
                    : "text-gray-600 hover:text-gray-900 hover:bg-black/5"
                }`}>
                Sign In
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center md:hidden">
              <button
                type="button"
                onClick={toggleMobileMenu}
                className={
                  navbarBackground === "black"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }>
                <span className="sr-only">Open main menu</span>
                {/* Menu icon */}
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            onClick={closeMobileMenu}></div>

          {/* Menu panel */}
          <div className="fixed right-0 top-0 bottom-0 w-3/4 max-w-xs bg-white shadow-xl flex flex-col">
            {/* Close button */}
            <div className="p-4 flex justify-end">
              <button
                type="button"
                onClick={closeMobileMenu}
                className="text-gray-500 hover:text-gray-900">
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Nav links */}
            <div className="px-6 py-4 flex-1">
              <div className="flex flex-col space-y-6">
                <Link
                  to="/hyperapp"
                  onClick={closeMobileMenu}
                  className="text-gray-800 hover:text-primary text-lg font-medium">
                  HyperApp
                </Link>
                <Link
                  to="/security"
                  onClick={closeMobileMenu}
                  className="text-gray-800 hover:text-primary text-lg font-medium">
                  Security
                </Link>
                <Link
                  to="/gold"
                  onClick={closeMobileMenu}
                  className="text-gray-800 hover:text-primary text-lg font-medium">
                  Gold
                </Link>
                <Link
                  to="/"
                  onClick={closeMobileMenu}
                  className="text-gray-800 hover:text-primary text-lg font-medium">
                  Blog
                </Link>
                <Link
                  to="/faq"
                  onClick={closeMobileMenu}
                  className="text-gray-800 hover:text-primary text-lg font-medium">
                  FAQs
                </Link>
                <Link
                  to="/terms"
                  onClick={closeMobileMenu}
                  className="text-gray-800 hover:text-primary text-lg font-medium">
                  Terms & Conditions
                </Link>
                <Link
                  to="/card-terms"
                  onClick={closeMobileMenu}
                  className="text-gray-800 hover:text-primary text-lg font-medium">
                  Card Terms
                </Link>
                <Link
                  to="/privacy"
                  onClick={closeMobileMenu}
                  className="text-gray-800 hover:text-primary text-lg font-medium">
                  Privacy Policy
                </Link>
              </div>
            </div>

            {/* Action buttons */}
            <div className="p-6 border-t border-gray-200">
              <div className="flex flex-col space-y-4">
                <a
                  href="https://app.basedapp.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}
                  className="block w-full text-center px-4 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50">
                  Sign In
                </a>
                <a
                  href="https://app.basedapp.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}
                  className="block w-full text-center px-4 py-2 bg-gradient-to-r from-primary to-secondary text-white rounded-full hover:opacity-90">
                  Get a Card
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="pt-20">{children}</main>

      {/* Footer - Added to the Layout component */}
      <footer className="bg-gray-50 border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center text-primary font-bold text-xl mb-4">
                <Link to="/" className="flex items-center">
                  <img
                    src={basedPrimaryLogo}
                    alt="BasedApp Logo"
                    className="h-8 w-auto mr-2"
                  />
                </Link>
              </div>
              <p className="text-gray-600 mb-4">
                Buy, sell and spend crypto anywhere in the world. Trade spot and perpetual futures using your non-custodial wallet on Hyperliquid.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/hyperapp"
                    className="text-gray-600 hover:text-primary">
                    HyperApp
                  </Link>
                </li>
                <li>
                  <Link
                    to="/security"
                    className="text-gray-600 hover:text-primary">
                    Security
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="text-gray-600 hover:text-primary">
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link to="/" className="text-gray-600 hover:text-primary">
                    Blog
                  </Link>
                </li>
                <li>
                  <Link
                    to="/waitlist"
                    onClick={() =>
                      trackEvent("Navigation", "Click", "Waitlist from Footer")
                    }
                    className="text-gray-600 hover:text-primary">
                    Waitlist
                  </Link>
                </li>
                <li>
                  <Link
                    to="https://drive.google.com/drive/folders/16IXfdKFUp8UKyMvT3gWd7mItqgRDDE2a?usp=sharing"
                    onClick={() =>
                      trackEvent("Navigation", "Click", "Waitlist from Footer")
                    }
                    className="text-gray-600 hover:text-primary">
                    Brand Assets
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/terms"
                    className="text-gray-600 hover:text-primary">
                    Platform T&Cs
                  </Link>
                </li>
                <li>
                  <Link
                    to="/card-terms"
                    className="text-gray-600 hover:text-primary">
                    Card T&Cs
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="text-gray-600 hover:text-primary">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Guide</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/2025-03-01-deposit-from-solana"
                    className="text-gray-600 hover:text-primary">
                    Deposit from Solana
                  </Link>
                </li>
                <li>
                  <Link
                    to="/2025-03-01-deposit-from-arbitrum"
                    className="text-gray-600 hover:text-primary">
                    Deposit from Arbitrum or Hyperliquid
                  </Link>
                </li>
                <li>
                  <Link
                    to="/2025-03-01-deposit-from-polygon"
                    className="text-gray-600 hover:text-primary">
                    Deposit from Polygon
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Connect</h3>
              <div className="flex space-x-4">
                <a
                  href="https://x.com/BasedAppHQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-primary">
                  <X className="w-6 h-6" />
                </a>
                <a
                  href="https://t.me/basedappupdates"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-primary">
                  <svg
                    className="w-6 h-6"
                    viewBox="0 0 24 24"
                    fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.041-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.178.119.13.154.305.164.43-.001.097-.015.185-.049.336z" />
                  </svg>
                </a>
                <a
                  href="https://www.linkedin.com/company/basedapp/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-primary">
                  <Linkedin className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>

          {/* Legal Disclaimer */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-center text-gray-600">
              © {new Date().getFullYear()} BasedApp. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
